"use client";

import { defaultMessages, type KitseedMessages } from "../lib/provider";
import { createContext } from "react";

export const FormErrorContext = createContext<{
  fields: Record<string, string>;
  setSummaryTarget: (node: HTMLDivElement | null) => void;
  setField: (name: string, message: string) => void;
} | null>(null);
export const FieldContext = createContext<{
  id: string;
  hintId?: string;
  labelId: string;
  errorId: string;
  invalid: boolean;
} | null>(null);

export function validationMessage(
  control: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
  messages: KitseedMessages = defaultMessages,
): string {
  const v = control.validity;
  if (v.valid || control.disabled) return "";
  if (v.valueMissing) return messages.required;
  if (v.typeMismatch)
    return control instanceof HTMLInputElement && control.type === "email"
      ? messages.invalidEmail
      : messages.invalidUrl;
  if (v.tooShort) return messages.tooShort((control as HTMLInputElement).minLength);
  if (v.tooLong) return messages.tooLong((control as HTMLInputElement).maxLength);
  if (v.rangeUnderflow) return messages.rangeUnderflow((control as HTMLInputElement).min);
  if (v.rangeOverflow) return messages.rangeOverflow((control as HTMLInputElement).max);
  return messages.invalidValue;
}

export function focusFormField(form: HTMLFormElement, key: string) {
  const controls = Array.from(form.elements).filter(
    (node): node is HTMLElement => node instanceof HTMLElement,
  );
  const control = controls.find((node) => node.id === key || node.getAttribute("name") === key);
  const target = control?.getClientRects().length
    ? control
    : (control?.closest("[data-field]")?.querySelector<HTMLElement>("[data-field-control]") ??
      control);
  if (!target) return;
  for (let node = target.parentElement; node && node !== form; node = node.parentElement) {
    if (node.tagName === "DETAILS") (node as HTMLDetailsElement).open = true;
  }
  const visible = target.getClientRects().length
    ? target
    : target.closest<HTMLElement>("fieldset, [data-field]");
  if (!visible) return;
  if (visible === target) target.focus({ preventScroll: true });
  else {
    visible.tabIndex = -1;
    visible.focus({ preventScroll: true });
  }
  visible.scrollIntoView({ block: "center", behavior: "instant" });
}
